import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { DashboardSubscriber } from '../types/gotypes'

interface ListSubscriptionsResponse {
  subscriptions: DashboardSubscriber[]
  count: number
}

// useMySubscriptions backs the "My subscriptions" page (#964 v1): every
// dashboard_subscribers row for the current user, active and tombstoned
// alike, with toggles that call the same self-subscribe/self-unsubscribe
// routes DashboardView's notify dialog uses.
export const useMySubscriptions = ({ customer }: { customer: string }) => {
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const queryKey = ['my-subscriptions', { customer }]

  const listQuery = useQuery({
    queryKey,
    enabled: Boolean(customer),
    queryFn: async () => {
      const res = await axios.get<ListSubscriptionsResponse>(
        `${API_BASE_URL}/notifications/subscriptions`,
        { params: { customer } }
      )
      return res.data.subscriptions
    },
  })

  const unsubscribeMutation = useMutation({
    mutationFn: async (dashboardId: string) => {
      await axios.delete(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/subscribers/me`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const resubscribeMutation = useMutation({
    mutationFn: async (dashboardId: string) => {
      await axios.post(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/subscribers/me`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const unsubscribe = useCallback((dashboardId: string) => unsubscribeMutation.mutateAsync(dashboardId), [unsubscribeMutation])
  const resubscribe = useCallback((dashboardId: string) => resubscribeMutation.mutateAsync(dashboardId), [resubscribeMutation])

  return {
    subscriptions: (listQuery.data || []) as DashboardSubscriber[],
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,

    unsubscribe,
    resubscribe,
    toggleIsLoading: unsubscribeMutation.isPending || resubscribeMutation.isPending,
  }
}

export default useMySubscriptions
